<template>
  <BaseExpansionPanel
    bold-label
    :label="getLocaleText('stream_filters_media_categories.category')"
  >
    <div class="tw:flex tw:flex-col tw:gap-2">
      <div>
        <Checkbox
          v-model="selectAll"
          :color="medium"
          data-testid="select-all"
          dense
          :indeterminate="selectAllIndeterminate"
          :label="getLocaleText('global.select_all')"
          new-design
          @update:model-value="updateSelectAll"
        />
      </div>
      <div
        v-for="category in mappedCategories"
        :key="category.id"
      >
        <Checkbox
          v-model="localCategoryKeys"
          :color="medium"
          :data-testid="`category-${category.id}`"
          dense
          :label="category.name"
          new-design
          :value="category.value"
          @update:model-value="updateCategoryKeys"
        />
      </div>
    </div>
  </BaseExpansionPanel>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { type Ref, computed, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseExpansionPanel } from "shared/components/base";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import { MediumField } from "shared/helpers/media";
import type { Category as CategoryResource } from "shared/resources";
import useGlobalStore from "shared/stores/global";
import type { ConvertKeysToSnakeCase } from "shared/types";

interface StreamFiltersMediaProps {
  medium: MediumField;
}

interface Category
  extends ConvertKeysToSnakeCase<Omit<CategoryResource, "id">> {
  id: number;
  value: string;
}

const categoryKeys = defineModel<string[]>("categoryKeys", {
  default: () => [],
});

const props = defineProps<StreamFiltersMediaProps>();

const globalStore = useGlobalStore();

const { categories, socialCategories } = storeToRefs(
  globalStore
) as unknown as {
  categories: Ref<Category[]>;
  socialCategories: Ref<Category[]>;
};

const selectAll = ref<boolean>(false);
const localCategoryKeys = ref<string[]>([]);

const localCategories = computed(() =>
  props.medium === MediumField.SOCIAL
    ? socialCategories.value
    : categories.value
);

const mappedCategories = computed(() =>
  localCategories.value.map((category) => ({
    value: `${props.medium}_${category.id}`,
    name: category.name,
    id: category.id,
  }))
);

const mappedCategoryKeys = computed<string[]>(() =>
  mappedCategories.value.map((mappedCategory) => mappedCategory.value)
);

const filteredCategoryKeys = computed<string[]>(() =>
  categoryKeys.value.filter(
    (categoryKey) => !categoryKey.includes(props.medium)
  )
);

const selectAllIndeterminate = computed(() =>
  Boolean(
    localCategoryKeys.value.length &&
      mappedCategoryKeys.value.length !== localCategoryKeys.value.length
  )
);

function updateCategoryKeys(updatedCategoryKeys: string[]) {
  selectAll.value =
    localCategoryKeys.value.length === mappedCategoryKeys.value.length;

  if (!selectAll.value && updatedCategoryKeys.length) {
    categoryKeys.value = filteredCategoryKeys.value.concat(
      ...updatedCategoryKeys
    );
  } else {
    categoryKeys.value = filteredCategoryKeys.value;
  }
}

function updateSelectAll() {
  if (
    !localCategoryKeys.value.length ||
    localCategoryKeys.value.length !== mappedCategoryKeys.value.length
  ) {
    localCategoryKeys.value = [...mappedCategoryKeys.value];
  } else {
    localCategoryKeys.value = [];
  }

  updateCategoryKeys([]);
}

watch(
  () => props.medium,
  () => {
    const selectedKeys = categoryKeys.value.filter((categoryKey) =>
      categoryKey.includes(props.medium)
    );

    localCategoryKeys.value = selectedKeys.length
      ? selectedKeys
      : [...mappedCategoryKeys.value];

    selectAll.value =
      !localCategoryKeys.value.length ||
      localCategoryKeys.value.length === mappedCategoryKeys.value.length;
  },
  { immediate: true }
);
</script>
